<template>
    <DCardBody class="p-3 mb-2 bg-warning text-white">
        {{ __('tools.emails.messenger_app_warning_message') }}
    </DCardBody>
    <DCard
        padding
    >
        <DForm @submit.prevent="submitForm">
            <div class="mb-5">
                <h4>
                    {{ __(`tools.emails.email.${template.name}.name`) }}
                </h4>
                <span class="text-muted fst-italic">
                    {{ __(`tools.emails.email.${template.name}.description`) }}
                </span>
            </div>
            <div class="mb-1">
                <span class="text-muted fst-italic mb-1">Messenger APP Template Name:</span>
                <h5>
                    {{ template.name.toUpperCase() }}
                </h5>
            </div>
            <DDualListSelector
                v-model="selected"
                :groups="groups"
                :available-items-label="__('tools.emails.available_placeholders')"
                :selected-items-label="__('tools.emails.selected_placeholders')"
            />
            <DFormControls
                :dirty="form.isDirty"
                :disabled="!isValidated"
                :cancel-route="route('tools.emails.index')"
                @submit="submitForm"
            >
                <template #additional_button_before_submit>
                    <DFab
                        icon="check-circle"
                        :label="__('tools.emails.messenger_app_validate')"
                        :disabled="isValidating"
                        @click="validateTemplate"
                    />
                </template>
            </DFormControls>
        </DForm>
    </DCard>
    <DModal
        :show="showValidationErrorModal"
        is-error
        is-medium
        :title="__('tools.emails.messenger_app_validation_errors_title')"
        :message="__('tools.emails.messenger_app_validation_errors_message', {'template_name': props.template.name.toUpperCase()})"
        @close="showValidationErrorModal = false"
    >
        <template #default>
            <DCard
                padding
            >
                <ul
                    v-if="Array.isArray(messengerValidationErrors)"
                    class="ul-class"
                >
                    <li
                        v-for="(error, index) in messengerValidationErrors"
                        :key="index"
                        class="pb-2 pl-0"
                    >
                        {{ error }}
                    </li>
                </ul>
                <p v-else>
                    {{ messengerValidationErrors }}
                </p>
            </DCard>
        </template>
    </DModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    template: {
        type: Object,
        default: () => ({})
    },
    placeholders: {
        type: Object,
        default: () => ({})
    },
    selectedPlaceholders: {
        type: Array,
        default: () => []
    }
})

const groups = computed(() => Object.entries(props.placeholders).map(([category, items]) => ({
    name: category,
    items: items.map(item => ({
        value: item.placeholder,
        label: item.placeholder,
        description: item.description
    }))
})))

const selected = ref(props.selectedPlaceholders.map(key => ({ value: `{${key}}`, label: `{${key}}` })))

const form = useForm({
    template_name: props.template.name,
    placeholders: selected.value.map(item => item.value)
})

const isValidated = ref(false)
const isValidating = ref(false)
const messengerValidationErrors = ref(null)
const showValidationErrorModal = ref(false)

watch(selected, () => {
    isValidated.value = false
}, { deep: true })

function validateTemplate () {
    isValidating.value = true

    window.axios.post(route('tools.emails.messenger-app.validate-template'), {
        template_name: props.template.name,
        placeholders: selected.value.map(item => item.value)
    })
        .then(() => {
            isValidated.value = true
            usePage().props.flash.message = __('tools.emails.messenger_app_validation_success')
        })
        .catch((error) => {
            isValidated.value = false

            if (error.response?.data?.messenger_errors !== undefined) {
                messengerValidationErrors.value = error.response.data.messenger_errors
                showValidationErrorModal.value = true
            } else {
                usePage().props.flash.errorMessages = error.response?.data?.error ?? error.message
            }
        })
        .finally(() => {
            isValidating.value = false
        })
}

function submitForm () {
    form.placeholders = selected.value.map(item => item.value)
    form.post(route('tools.emails.messenger-app.update-placeholders'))
}
</script>

<style lang="scss" scoped>
.ul-class{
    padding-left: 20px;
}
</style>
